import React from "react";
import Box from "@mui/material/Box";
import {
  DataGrid,
  gridClasses,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";

const LogisticRfqTable = ({
  rfqs,
  activeTab,
  isLoading,
  dashboardPath = "logistic-dashboard",
}) => {
  const navigate = useNavigate();

  const handleCheck = (id) => {
    navigate(`/${dashboardPath}/${id}`);
    localStorage.setItem("logisticActiveTab", activeTab);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const columns = [
    {
      field: "logisticDraftId",
      headerName: "Logistics RFQ ID",
      flex: 1,
      renderCell: (params) => (
        <span
          className="table-title"
          onClick={() => handleCheck(params.row.logisticDraftId)}
        >
          {params.row.logisticDraftId}
        </span>
      ),
    },
    { field: "modeOfConsignment", headerName: "Mode of Consignment", flex: 1 },
    { field: "consignee", headerName: "Consignee", flex: 1 },
    { field: "origin", headerName: "Origin", flex: 1 },
    { field: "destination", headerName: "Destination", flex: 1 },
    // { field: "transitTime", headerName: "Transit Time", flex: 1 },
    // { field: "volumeCBM", headerName: "Volume (CBM)", flex: 1 },
    {
      field: "submissionDate",
      headerName: "Submission Date",
      flex: 1,
      renderCell: (params) => {
        const { rebidEndDate, submissionDate } = params.row;
        return formatDateTime(rebidEndDate || submissionDate);
      },
    },
    // Quote status badge — shown on ALL tab AND RECOMMENDATION (matching RFQ "In Quotes" behavior)
    activeTab === "PROGRESS" || activeTab === "RECOMMENDATION"
      ? {
          field: "quoteStatus",
          headerName: "Quote Status",
          flex: 1.5,
          renderCell: (params) => {
            const statusColors = {
              SUBMITTED: "#35D85B",
              APPROVED_1: "#009688",
              APPROVED_2: "#BEB3B3",
              APPROVED: "#35D85B",
              AWARDED: "#0381F3",
              REJECTED: "#CB856B",
              DISQUALIFIED: "#CB856B",
              REBID: "#FF9800",
              REBIDDING: "#FF9800",
              EAUCTIONED: "#FF9800",
              BIDS_CLOSED: "#BEB3B3",
              ACCEPTING_BIDS: "#009688",
              NO_QUOTES: "#9E9E9E", // Gray for no quotes yet
            };

            const statusLabels = {
              SUBMITTED: "In Review by Buyer",
              APPROVED_1: "Approved by Buyer",
              APPROVED_2: "Approved by Purchase Manager",
              APPROVED: "Approved by Final Approver 1",
              AWARDED: "Awarded",
              REJECTED: "Rejected",
              DISQUALIFIED: "Rejected",
              REBID: "Revised",
              REBIDDING: "REBIDDING",
              EAUCTIONED: "Sent for E-Auction",
              BIDS_CLOSED: "RFQ CLOSED",
              ACCEPTING_BIDS: "Accepting Quotes",
              NO_QUOTES: "Awaiting Quotes",
            };

            const quoteStatus = (params.row.quoteStatus || "").toUpperCase();
            const rfqStatus = (params.row.status || "").toUpperCase();

            const displayStatus = statusLabels[quoteStatus]
              ? quoteStatus
              : statusLabels[rfqStatus]
                ? rfqStatus
                : "NO_QUOTES";

            return (
              <span
                style={{
                  background: statusColors[displayStatus],
                  borderRadius: "15px",
                  padding: "5px 15px",
                  fontWeight: 600,
                }}
              >
                {statusLabels[displayStatus]}
              </span>
            );
          },
        }
      : false,

    // ACTION tab — "Review Quotes" button (like BIDS tab in BuyerDashboard)
    activeTab === "ACTION" && {
      field: "action",
      headerName: "Action",
      flex: 1.2,
      renderCell: (params) => (
        <Button
          className="btn-contained"
          sx={{
            margin: "0 !important",
            fontSize: "12px",
            px: 9,
            width: "0 !important",
          }}
          onClick={() =>
            navigate(
              `/${dashboardPath}/view-quotes/${params.row.logisticDraftId}`,
            )
          }
        >
          Review Quotes
        </Button>
      ),
    },

    // RECOMMENDATION tab — "View" button (like QUOTES tab in RFQTable)
    activeTab === "RECOMMENDATION" && {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <Button
          className="btn-outlined"
          sx={{
            margin: "0 !important",
            padding: "5px !important",
            width: "auto !important",
            fontSize: "12px !important",
          }}
          onClick={() =>
            navigate(
              `/${dashboardPath}/view-quotes/${params.row.logisticDraftId}`,
            )
          }
        >
          View Quotes
        </Button>
      ),
    },

    // ALL tab — "View Quotes" button
    (activeTab === "PROGRESS" || activeTab === "CONCLUDED") && {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <Button
          className="btn-contained"
          sx={{
            margin: "0 !important",
            fontSize: "12px",
            px: 9,
            width: "0 !important",
          }}
          onClick={() =>
            navigate(
              `/${dashboardPath}/view-quotes/${params.row.logisticDraftId}`,
            )
          }
        >
          View Quotes
        </Button>
      ),
    },
  ].filter(Boolean);

  const rows = rfqs ? rfqs.map((rfq) => ({ ...rfq })) : [];

  return (
    <Box sx={{ width: "100%", minHeight: "400px" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10]}
        disableRowSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        disableColumnMenu
        disableColumnResize
        slots={{ toolbar: CustomToolbar }}
        getRowId={(row) => row.logisticDraftId}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        sx={{
          position: "relative",
          border: "none",
          fontFamily: "Inter !important",
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "700 !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            background: "none !important",
            color: "black",
            fontSize: "14px",
            borderRadius: "10px",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#fff",
          },
          "& .MuiDataGrid-cell": {
            fontSize: "13px",
            border: "none",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .css-1ccsd5i-MuiButtonBase-root-MuiButton-root": {
            color: "#000 !important",
            border: "1px solid #B0B0B0",
            borderRadius: "12px",
            padding: 1,
          },
          "& .MuiInputBase-root-MuiInput-root::after": {
            borderBottom: "none !important",
          },
          "& .MuiInputBase-root-MuiInput-root": {
            borderBottom: "none !important",
          },
          [`& .${gridClasses.row}.even`]: {
            backgroundColor: "#EAF4FF",
          },
          "& .MuiInput-underline:before": {
            borderBottom: "none !important",
          },
          "& .css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
            borderBottom: "none !important",
          },
        }}
      />
    </Box>
  );
};

export default LogisticRfqTable;

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ display: "flex", justifyContent: "end" }}>
      <GridToolbarQuickFilter
        sx={{
          border: "1px solid #B0B0B0",
          borderRadius: "12px",
          padding: "4px 12px",
          "&:hover": { backgroundColor: "#D8E9F0" },
          "& .MuiInputBase-root": { borderRadius: "12px" },
          width: 300,
        }}
      />
    </GridToolbarContainer>
  );
}
